import { useEffect, useRef, useState } from "react";
import { callMcp, type McpToolResult } from "../lib/mcp";

// Embeds one of the server's MCP Apps widgets (infra/lib/lambdas/mcp/
// widgets.ts) in a sandboxed iframe and plays the HOST side of the
// postMessage handshake the widget bootstrap (WIDGET_BOOTSTRAP_JS) expects:
//
//   1) widget -> host:  ui/initialize request
//   2) host  -> widget: result { protocolVersion, hostInfo, hostContext }
//   3) widget -> host:  ui/notifications/initialized
//   4) host  -> widget: ui/notifications/tool-result { structuredContent }
//   5) widget -> host:  ui/notifications/size-changed -> resize the frame
//   6) widget -> host:  tools/call requests -> proxied to POST /mcp
//      (JSON-RPC, stored JWT) and answered with the matching request id
//
// The widget HTML is served same-origin at GET /mcp/ui/<name> (CloudFront
// proxies /mcp in prod; the vite dev server proxies it too). Degradable by
// design: if the iframe errors or the handshake never completes within 5s,
// the frame hides itself and the page's native UI stands alone.

const HANDSHAKE_TIMEOUT_MS = 5_000;
const PROTOCOL_VERSION = "2025-11-25"; // mirrors infra/lib/lambdas/mcp/server.ts
const DEFAULT_HEIGHT = 180;

type JsonRpcMessage = {
  jsonrpc?: string;
  id?: string | number | null;
  method?: string;
  params?: Record<string, unknown>;
  result?: unknown;
  error?: { code: number; message: string };
};

type Phase = "connecting" | "ready" | "failed";

export function WidgetFrame({
  widget,
  data,
  jwt,
}: {
  /** Widget name as served at /mcp/ui/<name>, e.g. "session-status". */
  widget: string;
  /** structuredContent payload — same shape the paired MCP tool returns. */
  data: Record<string, unknown> | null;
  /** Stored JWT for proxying the widget's bridge tools/call to POST /mcp. */
  jwt: string | null;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [phase, setPhase] = useState<Phase>("connecting");
  const [height, setHeight] = useState(DEFAULT_HEIGHT);

  // Refs so the (mount-once) message listener always sees current values
  // without re-running the effect — re-mounting would reload the iframe.
  const initializedRef = useRef(false);
  const jwtRef = useRef<string | null>(jwt);
  jwtRef.current = jwt;
  const dataRef = useRef<Record<string, unknown> | null>(data);
  dataRef.current = data;
  // Dedupe pushes: the parent re-renders on every poll tick; only re-send
  // tool-result when the payload actually changed.
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    let disposed = false;

    const post = (msg: Record<string, unknown>) => {
      try {
        // The sandboxed (opaque-origin) iframe requires "*" as target.
        iframe.contentWindow?.postMessage(msg, "*");
      } catch {
        /* iframe gone */
      }
    };

    const sendToolResult = () => {
      const payload = dataRef.current;
      if (payload == null) return;
      lastSentRef.current = JSON.stringify(payload);
      post({
        jsonrpc: "2.0",
        method: "ui/notifications/tool-result",
        params: { structuredContent: payload },
      });
    };

    const answerToolCall = (id: string | number, params: Record<string, unknown>) => {
      const name = params.name;
      const args =
        params.arguments && typeof params.arguments === "object"
          ? (params.arguments as Record<string, unknown>)
          : {};
      const token = jwtRef.current;
      if (typeof name !== "string" || !name) {
        post({
          jsonrpc: "2.0",
          id,
          error: { code: -32602, message: "tools/call requires a tool name" },
        });
        return;
      }
      if (!token) {
        post({
          jsonrpc: "2.0",
          id,
          error: { code: -32000, message: "not signed in" },
        });
        return;
      }
      // Same JSON-RPC code path Claude uses, from inside the dashboard. The
      // raw tool result (structuredContent + content) goes back verbatim —
      // the widget's bridgeOutput() unwraps it.
      callMcp<McpToolResult<unknown>>(token, "tools/call", {
        name,
        arguments: args,
      })
        .then((result) => {
          if (!disposed) post({ jsonrpc: "2.0", id, result });
        })
        .catch((err: unknown) => {
          if (!disposed)
            post({
              jsonrpc: "2.0",
              id,
              error: {
                code: -32000,
                message: err instanceof Error ? err.message : "tool call failed",
              },
            });
        });
    };

    const onMessage = (ev: MessageEvent) => {
      if (ev.source !== iframe.contentWindow) return;
      const msg = ev.data as JsonRpcMessage;
      if (!msg || msg.jsonrpc !== "2.0" || typeof msg.method !== "string") return;
      const params: Record<string, unknown> =
        msg.params && typeof msg.params === "object" ? msg.params : {};

      if (msg.method === "ui/initialize" && msg.id != null) {
        post({
          jsonrpc: "2.0",
          id: msg.id,
          result: {
            protocolVersion: PROTOCOL_VERSION,
            hostInfo: { name: "nightswarm-dashboard", version: "1.0.0" },
            hostContext: { theme: "dark" },
          },
        });
        initializedRef.current = true;
        setPhase("ready");
        sendToolResult();
        return;
      }
      if (msg.method === "ui/notifications/initialized") {
        // Handshake complete — (re-)deliver so a widget that wired render()
        // after its initialize round-trip still gets the data.
        sendToolResult();
        return;
      }
      if (msg.method === "ui/notifications/size-changed") {
        const h = Number(params.height);
        if (Number.isFinite(h) && h > 0) setHeight(Math.ceil(h));
        return;
      }
      if (msg.method === "tools/call" && msg.id != null) {
        answerToolCall(msg.id, params);
        return;
      }
    };

    window.addEventListener("message", onMessage);
    const timer = setTimeout(() => {
      if (!initializedRef.current) setPhase("failed");
    }, HANDSHAKE_TIMEOUT_MS);

    return () => {
      disposed = true;
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };
  }, [widget]);

  // Push refreshed data into the live frame (the page's poll keeps `data`
  // current). Skipped until the handshake lands and when nothing changed.
  useEffect(() => {
    if (phase !== "ready" || data == null) return;
    const json = JSON.stringify(data);
    if (json === lastSentRef.current) return;
    lastSentRef.current = json;
    try {
      iframeRef.current?.contentWindow?.postMessage(
        {
          jsonrpc: "2.0",
          method: "ui/notifications/tool-result",
          params: { structuredContent: data },
        },
        "*"
      );
    } catch {
      /* iframe gone */
    }
  }, [data, phase]);

  if (phase === "failed") return null;

  return (
    <div
      className={`widget-frame${phase === "connecting" ? " widget-frame-connecting" : ""}`}
      style={{ height }}
    >
      <iframe
        ref={iframeRef}
        title={widget}
        src={`/mcp/ui/${encodeURIComponent(widget)}`}
        sandbox="allow-scripts"
        onError={() => setPhase("failed")}
      />
    </div>
  );
}
